<template>
  <div class="shop-order-page">
    <nav_list :index="4"></nav_list>

    <div class="order-wrap">
      <div v-if="loading" class="state-card">
        <i class="el-icon-loading"></i>
        <span>预订信息加载中...</span>
      </div>

      <div v-else-if="!requestData" class="state-card state-card--empty">
        <div class="state-main">
          <div class="state-title">没有可用的预订信息</div>
          <div class="state-desc">请先从商品详情页选择套餐和出行配置，再进入订单填写页。</div>
          <el-button type="primary" @click="jump('/shop')">返回商城</el-button>
        </div>
      </div>

      <template v-else>
        <div class="breadcrumb-row">
          <el-page-header @back="goBack" content="填写预订信息"></el-page-header>
        </div>

        <div ref="orderLayout" class="order-layout">
          <div class="order-main">
            <section class="order-card hero-card">
              <div class="hero-head">
                <div>
                  <div class="hero-badge">预订确认</div>
                  <h1 class="hero-title">{{ productTitle }}</h1>
                  <div class="hero-subtitle">{{ packageTitle }}</div>
                </div>
                <div class="hero-price">
                  <span class="price-prefix">¥</span>
                  <span class="price-value">{{ formatMoney(orderTotalPrice) }}</span>
                </div>
              </div>

              <div class="hero-meta">
                <div class="meta-chip" v-if="shouldShowServiceDate()">
                  <span class="meta-label">服务日期</span>
                  <span class="meta-value">{{ booking.s_date || '-' }}</span>
                </div>
                <div class="meta-chip">
                  <span class="meta-label">购买份数</span>
                  <span class="meta-value">{{ totalQuantity }} {{ orderUnit || '份' }}</span>
                </div>
                <div class="meta-chip" v-if="requestData.event_time">
                  <span class="meta-label">出行时段</span>
                  <span class="meta-value">{{ requestData.event_time }}</span>
                </div>
              </div>

              <div v-if="selectedSpecTexts.length" class="selected-specs">
                <div class="inline-title">已选规格</div>
                <div class="spec-list">
                  <span v-for="item in selectedSpecTexts" :key="item" class="spec-tag">{{ item }}</span>
                </div>
              </div>
            </section>

            <section v-if="refundPolicyLines.length" class="order-card">
              <div class="section-title">取消政策</div>
              <ul class="policy-list">
                <li v-for="(item, index) in refundPolicyLines" :key="'policy-' + index">
                  {{ item }}
                </li>
              </ul>
            </section>

            <shop-order-buyer-section
              :booking="booking"
              :booking-field="bookingField"
              :show-guide-lang="showGuideLang"
              :show-buyer-field-fn="showBuyerField"
              :get-field-options-fn="getFieldOptions"
              :get-option-label-fn="getOptionLabel"
              :get-option-value-fn="getOptionValue"
            />

            <shop-order-traveler-section
              :traveler-customers="travelerCustomers"
              :get-customer-title-fn="getCustomerTitle"
              :get-visible-custom-fields-fn="getVisibleCustomFields"
              :get-field-mode-fn="getFieldMode"
              :is-wide-field-fn="isWideField"
              :get-field-label-fn="getFieldLabel"
              :is-required-fn="isRequired"
              :get-field-options-fn="getFieldOptions"
              :get-option-value-fn="getOptionValue"
              :get-option-label-fn="getOptionLabel"
              :get-country-parent-fn="getCountryParent"
              :get-customer-index-fn="getCustomerIndex"
              :set-country-parent-fn="setCountryParent"
              :get-country-cities-fn="getCountryCities"
            />

            <shop-order-contact-section
              :support-customers="supportCustomers"
              :get-customer-title-fn="getCustomerTitle"
              :get-visible-custom-fields-fn="getVisibleCustomFields"
              :get-field-mode-fn="getFieldMode"
              :is-wide-field-fn="isWideField"
              :get-field-label-fn="getFieldLabel"
              :is-required-fn="isRequired"
              :get-field-options-fn="getFieldOptions"
              :get-option-value-fn="getOptionValue"
              :get-option-label-fn="getOptionLabel"
              :get-country-parent-fn="getCountryParent"
              :get-customer-index-fn="getCustomerIndex"
              :set-country-parent-fn="setCountryParent"
              :get-country-cities-fn="getCountryCities"
              :is-contact-phone-field-fn="isContactPhoneField"
              :is-contact-app-field-fn="isContactAppField"
              :should-skip-support-field-fn="shouldSkipSupportField"
              :get-contact-phone-enabled-fn="getContactPhoneEnabled"
              :set-contact-phone-enabled-fn="setContactPhoneEnabled"
              :get-custom-field-config-fn="getCustomFieldConfig"
              :get-contact-app-enabled-fn="getContactAppEnabled"
              :set-contact-app-enabled-fn="setContactAppEnabled"
              :get-supported-contact-apps-fn="getSupportedContactApps"
              :get-contact-app-option-value-fn="getContactAppOptionValue"
            />

            <shop-order-traffic-section
              :traffic-list="booking.traffic"
              :get-traffic-title-fn="getTrafficTitle"
              :get-visible-traffic-fields-fn="getVisibleTrafficFields"
              :is-wide-field-fn="isWideField"
              :get-field-label-fn="getFieldLabel"
              :is-required-fn="isRequired"
              :get-field-mode-fn="getFieldMode"
              :has-traffic-time-options-fn="hasTrafficTimeOptions"
              :on-traffic-field-change-fn="onTrafficFieldChange"
              :get-traffic-time-options-fn="getTrafficTimeOptions"
              :get-option-value-fn="getOptionValue"
              :get-option-label-fn="getOptionLabel"
              :get-traffic-field-options-fn="getTrafficFieldOptions"
              :get-selected-voucher-location-fn="getSelectedVoucherLocation"
              :get-voucher-location-time-lines-fn="getVoucherLocationTimeLines"
            />

            <section v-if="hasMobileDeviceFields" class="order-card">
              <div class="section-title">其他设备信息</div>
              <div class="form-grid">
                <div
                  v-for="field in mobileDeviceFields"
                  :key="'mobile-' + field.key"
                  class="form-item"
                  :class="{ 'form-item--full': isWideField(field.key) }"
                >
                  <label>{{ getFieldLabel(field.key, field.config) }}<span v-if="isRequired(field.config)" class="required">*</span></label>

                  <el-input
                    v-if="getFieldMode(field.config, field.key) === 'text'"
                    v-model.trim="booking.mobile_device[field.key]"
                    :placeholder="'请输入' + getFieldLabel(field.key, field.config)"
                  ></el-input>

                  <el-date-picker
                    v-else-if="getFieldMode(field.config, field.key) === 'date'"
                    v-model="booking.mobile_device[field.key]"
                    type="date"
                    value-format="yyyy-MM-dd"
                    placeholder="请选择日期"
                    style="width: 100%;"
                  ></el-date-picker>

                  <el-time-picker
                    v-else-if="getFieldMode(field.config, field.key) === 'time'"
                    v-model="booking.mobile_device[field.key]"
                    value-format="HH:mm:ss"
                    placeholder="请选择时间"
                    style="width: 100%;"
                  ></el-time-picker>

                  <el-switch
                    v-else-if="getFieldMode(field.config, field.key) === 'boolean'"
                    v-model="booking.mobile_device[field.key]"
                    :active-value="true"
                    :inactive-value="false"
                  ></el-switch>

                  <el-input
                    v-else-if="getFieldMode(field.config, field.key) === 'textarea'"
                    v-model.trim="booking.mobile_device[field.key]"
                    type="textarea"
                    :rows="3"
                    :placeholder="'请输入' + getFieldLabel(field.key, field.config)"
                  ></el-input>

                  <el-select
                    v-else
                    v-model="booking.mobile_device[field.key]"
                    filterable
                    placeholder="请选择"
                  >
                    <el-option
                      v-for="item in getFieldOptions(field.config, field.key)"
                      :key="'mobile-opt-' + field.key + '-' + getOptionValue(item)"
                      :label="getOptionLabel(item)"
                      :value="getOptionValue(item)"
                    />
                  </el-select>
                </div>
              </div>
            </section>

            <section class="order-card">
              <div class="section-title">订单备注</div>
              <el-input
                v-model.trim="booking.order_note"
                type="textarea"
                :rows="4"
                maxlength="900"
                show-word-limit
                placeholder="请输入备注信息，可留空"
              ></el-input>
            </section>
          </div>

          <aside
            ref="orderSide"
            :class="['order-side', { 'is-affixed': sideAffixed }]"
            :style="orderSideStyle"
          >
            <div ref="sideCard" class="side-card" :style="sideCardStyle">
              <div class="side-title">订单总览</div>
              <div class="side-price">¥ {{ formatMoney(orderTotalPrice) }}</div>
              <div class="side-list">
                <div class="side-row">
                  <span>商品</span>
                  <strong>{{ productTitle }}</strong>
                </div>
                <div class="side-row">
                  <span>套餐</span>
                  <strong>{{ packageTitle }}</strong>
                </div>
                <div class="side-row">
                  <span>数量</span>
                  <strong>{{ totalQuantity }} {{ orderUnit || '份' }}</strong>
                </div>
                <div v-if="shouldShowServiceDate()" class="side-row">
                  <span>日期</span>
                  <strong>{{ booking.s_date || '-' }}</strong>
                </div>
              </div>
              <div v-if="refundPolicyLines.length" class="side-note">
                {{ refundPolicyLines[0] }}
              </div>
              <el-button
                type="primary"
                class="submit-btn"
                :loading="submitting"
                @click="submitOrder"
              >
                {{ submitting ? '提交中...' : '提交预订' }}
              </el-button>
            </div>
          </aside>
        </div>

      </template>
    </div>
  </div>
</template>

<script>
import nav_list from '../index/nav_list.vue';
import ShopOrderBuyerSection from './components/ShopOrderBuyerSection.vue';
import ShopOrderTravelerSection from './components/ShopOrderTravelerSection.vue';
import ShopOrderContactSection from './components/ShopOrderContactSection.vue';
import ShopOrderTrafficSection from './components/ShopOrderTrafficSection.vue';
import shopOrderDataMixin from './mixins/shopOrderDataMixin';
import shopOrderFormMixin from './mixins/shopOrderFormMixin';
import shopOrderSubmitMixin from './mixins/shopOrderSubmitMixin';

export default {
  name: 'shop-order',
  mixins: [shopOrderDataMixin, shopOrderFormMixin, shopOrderSubmitMixin],
  components: {
    nav_list,
    ShopOrderBuyerSection,
    ShopOrderTravelerSection,
    ShopOrderContactSection,
    ShopOrderTrafficSection,
  },
  data() {
    return {
      loading: true,
      submitting: false,
      requestData: null,
      bookingField: {
        traffics: [],
        custom: {},
      },
      booking: {
        custom: [],
        traffic: [],
        mobile_device: {},
        buyer_first_name: '',
        buyer_last_name: '',
        buyer_country: 'CN',
        buyer_tel_country_code: '86',
        buyer_tel_number: '',
        buyer_Email: '',
        partner_order_no: '',
        guide_lang: '',
        order_note: '',
        pay_type: '01',
        s_date: '',
      },
      prodInfo: null,
      refundPolicy: null,
      countryAreas: [
        { country_code: 'CN', country_name: '中国', tel_area: '86' },
        { country_code: 'JP', country_name: '日本', tel_area: '81' },
        { country_code: 'US', country_name: '美国', tel_area: '1' },
        { country_code: 'HK', country_name: '中国香港', tel_area: '852' },
      ],
      countryParentMap: {},
      contactPhoneEnabledMap: {},
      contactAppEnabledMap: {},
      sideAffixed: false,
      sideCardWidth: 0,
      sideCardLeft: 0,
      sideCardTop: 0,
      sideCardHeight: 0,
      scrollContainers: [],
      handleSideScrollBound: null,
    };
  },
  computed: {
    productTitle() {
      return this.prodInfo && this.prodInfo.prod_name
        ? this.prodInfo.prod_name
        : this.requestData && this.requestData.extra && this.requestData.extra.prod_name
          ? this.requestData.extra.prod_name
          : '商品预订';
    },
    packageTitle() {
      return this.prodInfo && this.prodInfo.pkg_name
        ? this.prodInfo.pkg_name
        : this.requestData && this.requestData.extra && this.requestData.extra.pkg_name
          ? this.requestData.extra.pkg_name
          : '套餐信息';
    },
    orderTotalPrice() {
      return this.requestData && this.requestData.b2c_total_price
        ? Number(this.requestData.b2c_total_price)
        : 0;
    },
    totalQuantity() {
      if (!this.booking.skus || !this.booking.skus.length) return 0;
      return this.booking.skus.reduce((sum, item) => sum + Number(item.qty || 0), 0);
    },
    orderUnit() {
      return this.requestData && this.requestData.extra ? this.requestData.extra.unit || '' : '';
    },
    selectedSpecTexts() {
      const specs = this.requestData && this.requestData.extra ? this.requestData.extra.specs || [] : [];
      const specRefs = this.getSelectedSpecRefs();
      return specs.map((spec) => {
        const selected = (spec.spec_items || []).filter((item) => specRefs.indexOf(item.spec_item_oid) !== -1);
        if (!selected.length) return '';
        return spec.spec_title + '：' + selected.map((item) => item.name).join(' / ');
      }).filter(Boolean);
    },
    showGuideLang() {
      return this.bookingField
        && this.bookingField.guide_lang
        && this.isRequired(this.bookingField.guide_lang);
    },
    travelerCustomers() {
      return (this.booking.custom || []).filter((item) => item.cus_type === 'cus_01' || item.cus_type === 'cus_02');
    },
    supportCustomers() {
      return (this.booking.custom || []).filter((item) => item.cus_type !== 'cus_01' && item.cus_type !== 'cus_02');
    },
    hasMobileDeviceFields() {
      return this.mobileDeviceFields.length > 0;
    },
    mobileDeviceFields() {
      const fields = [];
      const config = this.bookingField && this.bookingField.mobile_device ? this.bookingField.mobile_device : {};
      Object.keys(config).forEach((key) => {
        if (config[key] && typeof config[key] === 'object') {
          fields.push({ key, config: config[key] });
        }
      });
      return fields;
    },
    refundPolicyLines() {
      const props = this.getPmdlProperties(this.effectiveRefundPolicy);
      if (!props) return [];
      if (props.partial_refund && props.partial_refund.list && props.partial_refund.list.length) {
        return props.partial_refund.list.map((item) => item.desc || '').filter(Boolean);
      }
      if (props.policy_type && props.policy_type.desc) {
        return [props.policy_type.desc];
      }
      return [];
    },
    effectiveRefundPolicy() {
      if (this.requestData && this.requestData.extra && this.requestData.extra.pkg_refund_policy) {
        return this.requestData.extra.pkg_refund_policy;
      }
      return this.refundPolicy;
    },
    isMobileLayout() {
      if (typeof window === 'undefined') return false;
      return window.innerWidth <= 992;
    },
    orderSideStyle() {
      if (this.isMobileLayout) {
        return {};
      }
      if (this.sideAffixed) {
        return {
          position: 'relative',
          minHeight: this.sideCardHeight ? this.sideCardHeight + 'px' : '',
        };
      }
      return {};
    },
    sideCardStyle() {
      if (this.isMobileLayout) {
        return {};
      }
      if (this.sideAffixed) {
        return {
          position: 'fixed',
          top: this.sideCardTop ? this.sideCardTop + 'px' : '',
          left: this.sideCardLeft ? this.sideCardLeft + 'px' : '',
          width: this.sideCardWidth ? this.sideCardWidth + 'px' : '',
          zIndex: 20,
        };
      }
      return {};
    },
  },
  mounted() {
    this.initPage();
    this.handleSideScrollBound = this.updateSideFloating.bind(this);
    this.$nextTick(() => {
      this.bindSideScroll();
      this.updateSideFloating();
      window.addEventListener('resize', this.handleSideScrollBound);
    });
  },
  beforeDestroy() {
    this.unbindSideScroll();
    if (this.handleSideScrollBound) {
      window.removeEventListener('resize', this.handleSideScrollBound);
    }
  },
  methods: {
    async initPage() {
      this.loading = true;
      try {
        const cached = this.cache('kkday_order_req');
        if (!cached) {
          this.requestData = null;
          return;
        }
        this.requestData = cached;
        await this.loadBookingData();
      } catch (error) {
        this.$message.error('加载订单页失败');
      } finally {
        this.loading = false;
        this.$nextTick(() => {
          this.bindSideScroll();
          this.updateSideFloating();
        });
      }
    },
    bindSideScroll() {
      this.unbindSideScroll();
      const candidates = [
        window,
        document,
        document.querySelector('#app .el-scrollbar__wrap'),
        document.scrollingElement,
      ].filter(Boolean);

      this.scrollContainers = candidates.filter((item, index) => candidates.indexOf(item) === index);
      this.scrollContainers.forEach((container) => {
        if (container && this.handleSideScrollBound) {
          container.addEventListener('scroll', this.handleSideScrollBound, { passive: true });
        }
      });
    },
    unbindSideScroll() {
      (this.scrollContainers || []).forEach((container) => {
        if (container && this.handleSideScrollBound) {
          container.removeEventListener('scroll', this.handleSideScrollBound);
        }
      });
      this.scrollContainers = [];
    },
    getScrollTop() {
      const wrap = document.querySelector('#app .el-scrollbar__wrap');
      const wrapTop = wrap && typeof wrap.scrollTop === 'number' ? wrap.scrollTop : 0;
      const windowTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
      const docTop = document.scrollingElement && typeof document.scrollingElement.scrollTop === 'number'
        ? document.scrollingElement.scrollTop
        : 0;
      return Math.max(wrapTop, windowTop, docTop);
    },
    updateSideFloating() {
      if (typeof window === 'undefined') return;
      if (this.isMobileLayout) {
        this.sideAffixed = false;
        this.sideCardWidth = 0;
        this.sideCardLeft = 0;
        this.sideCardTop = 0;
        this.sideCardHeight = 0;
        return;
      }

      const side = this.$refs.orderSide;
      const card = this.$refs.sideCard;
      if (!side || !card) return;

      const scrollTop = this.getScrollTop();
      const rect = side.getBoundingClientRect();
      this.sideCardWidth = side.offsetWidth;
      this.sideCardHeight = card.offsetHeight;

      if (!this.sideCardLeft || !this.sideCardTop || scrollTop <= 0) {
        this.sideCardLeft = rect.left;
        this.sideCardTop = rect.top;
      }

      this.sideAffixed = scrollTop > 0;
    },
    async loadBookingData() {
      const res = await this.get('/kkday/bookingf/load-data', this.requestData, 'post', false);
      this.bookingField = res && res.bookingfield ? res.bookingfield : { traffics: [], custom: {} };
      this.prodInfo = res && res.prod_info ? res.prod_info : null;
      this.refundPolicy = res && res.pmdl_refund_policy ? res.pmdl_refund_policy : null;

      const bookingRes = res && res.booking ? res.booking : {};
      this.booking = {
        custom: this.buildCustomData(bookingRes, this.bookingField),
        traffic: this.buildTrafficData(bookingRes, this.bookingField, this.requestData),
        mobile_device: this.buildMobileDeviceData(bookingRes, this.bookingField),
        buyer_first_name: bookingRes.buyer_first_name || '',
        buyer_last_name: bookingRes.buyer_last_name || '',
        buyer_country: bookingRes.buyer_country || 'CN',
        buyer_tel_country_code: '86',
        buyer_tel_number: bookingRes.buyer_tel_number || '',
        buyer_Email: bookingRes.buyer_Email || '',
        partner_order_no: bookingRes.partner_order_no || '',
        guide_lang: bookingRes.guide_lang || '',
        order_note: bookingRes.order_note || '',
        pay_type: bookingRes.pay_type || '01',
        s_date: bookingRes.s_date || this.requestData.s_date || '',
        e_date: bookingRes.e_date || this.requestData.e_date || '',
        skus: bookingRes.skus || this.requestData.skus || [],
      };

      this.initCountryParents();
      this.initContactToggles();
    },
    shouldShowServiceDate() {
      if (this.requestData && this.requestData.extra && this.requestData.extra.go_date_setting
        && this.requestData.extra.go_date_setting.type === '04') {
        return false;
      }
      if (this.prodInfo && this.prodInfo.go_date_setting && this.prodInfo.go_date_setting.type === '04') {
        return false;
      }
      return !!this.booking.s_date;
    },
    formatMoney(value) {
      return Number(value || 0).toFixed(2);
    },
    async submitOrder() {
      const errorMessage = this.validateBooking();
      if (errorMessage) {
        this.$message.warning(errorMessage);
        return;
      }

      this.submitting = true;
      try {
        const payload = this.buildSubmitPayload();
        const res = await this.get('/kkday/test_create', payload, 'post', false);
        if (res && res.sn) {
          this.cache('kkday_last_order_sn', res.sn);
          this.jump('/shop/pay', {
            sn: res.sn,
            from: 'checkout',
          });
        } else {
          this.$message.success('订单创建成功');
        }
      } catch (error) {
        this.$message.error('订单创建失败，请重试');
      } finally {
        this.submitting = false;
      }
    },
    goBack() {
      if (window.history.length > 1) {
        this.back();
      } else {
        this.jump('/shop/info', {
          prod_no: this.requestData ? this.requestData.prod_no : '',
          id: this.requestData ? this.requestData.prod_no : '',
        });
      }
    },
  },
};
</script>

<style scoped>
.shop-order-page {
  min-height: 100vh;
  background: #f5f7fb;
  padding-bottom: 40px;
}

.order-wrap {
  width: 1200px;
  margin: 20px auto 0;
}

.state-card,
.order-card,
.side-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 16px 1px rgb(0 0 0 / 6%);
}

.state-card {
  min-height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.state-card--empty {
  text-align: center;
}

.state-title {
  color: #223046;
  font-size: 24px;
  font-weight: bold;
}

.state-desc {
  margin: 12px 0 24px;
  color: #7b8798;
  font-size: 14px;
}

.breadcrumb-row {
  margin-bottom: 16px;
}

.order-layout {
  display: flex;
  align-items: flex-start;
  gap: 20px;
}

.order-main {
  flex: 1;
  min-width: 0;
}

.order-side {
  width: 300px;
  flex-shrink: 0;
  align-self: flex-start;
}

.order-side.is-affixed {
  position: relative;
}

.order-card {
  margin-bottom: 20px;
  padding: 24px;
}

.hero-card {
  padding: 28px;
  background: linear-gradient(135deg, #fbfdff 0%, #f1f6ff 100%);
}

.hero-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
}

.hero-badge {
  display: inline-block;
  padding: 5px 10px;
  border-radius: 999px;
  background: #eaf2ff;
  color: #1c5098;
  font-size: 12px;
}

.hero-title {
  margin: 14px 0 0;
  color: #223046;
  font-size: 30px;
  line-height: 1.4;
}

.hero-subtitle {
  margin-top: 10px;
  color: #718197;
  font-size: 15px;
}

.hero-price {
  flex-shrink: 0;
  color: #1c5098;
  font-weight: bold;
}

.price-prefix {
  font-size: 20px;
}

.price-value {
  font-size: 42px;
  line-height: 1;
}

.hero-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 20px;
}

.meta-chip {
  min-width: 180px;
  padding: 14px 16px;
  background: rgba(255, 255, 255, 0.88);
  border-radius: 10px;
}

.meta-label {
  display: block;
  color: #7b8798;
  font-size: 12px;
}

.meta-value {
  display: block;
  margin-top: 8px;
  color: #223046;
  font-size: 15px;
  font-weight: bold;
}

.selected-specs {
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px solid #e4ebf5;
}

.inline-title,
.section-title {
  color: #223046;
  font-size: 20px;
  font-weight: bold;
}

.spec-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 14px;
}

.spec-tag {
  padding: 6px 12px;
  border-radius: 999px;
  background: #edf4ff;
  color: #1c5098;
  font-size: 13px;
}

.policy-list {
  margin: 16px 0 0;
  padding-left: 20px;
  color: #5f6c80;
  line-height: 1.9;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px 20px;
  margin-top: 18px;
}

.form-item {
  min-width: 0;
}

.form-item--full {
  grid-column: 1 / -1;
}

.form-item label {
  display: block;
  margin-bottom: 10px;
  color: #314155;
  font-size: 14px;
  font-weight: bold;
}

.required {
  margin-left: 4px;
  color: #dd4c38;
}

.inline-field {
  display: flex;
  gap: 12px;
}

.inline-field--stack {
  margin-top: 12px;
}

.stack-field {
  display: grid;
  gap: 14px;
  margin-top: 12px;
}

.form-subitem label {
  margin-bottom: 8px;
  font-size: 13px;
}

.voucher-location-info {
  margin-top: 16px;
  padding: 16px 18px;
  border-radius: 10px;
  background: #f7fbff;
  border: 1px solid #dbe9fb;
}

.voucher-info-row {
  display: flex;
  gap: 12px;
  color: #516173;
  font-size: 13px;
  line-height: 1.8;
}

.voucher-info-row + .voucher-info-row {
  margin-top: 8px;
}

.voucher-info-label {
  flex: 0 0 72px;
  color: #223046;
  font-weight: bold;
}

.voucher-info-value {
  flex: 1;
  min-width: 0;
}

.side-card {
  box-sizing: border-box;
  width: 100%;
  padding: 22px;
}

.side-title {
  color: #223046;
  font-size: 18px;
  font-weight: bold;
}

.side-price {
  margin-top: 18px;
  color: #1c5098;
  font-size: 34px;
  font-weight: bold;
}

.side-list {
  margin-top: 18px;
  padding-top: 18px;
  border-top: 1px solid #e6edf7;
}

.side-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
  color: #6a778b;
  font-size: 13px;
}

.side-row strong {
  max-width: 160px;
  color: #223046;
  font-weight: bold;
  text-align: right;
}

.side-note {
  margin-top: 8px;
  padding: 12px 14px;
  border-radius: 8px;
  background: #f8fafc;
  color: #6a778b;
  font-size: 12px;
  line-height: 1.8;
}

.submit-btn {
  width: 100%;
  margin-top: 18px;
}

@media (max-width: 1260px) {
  .order-wrap {
    width: calc(100% - 24px);
  }
}

@media (max-width: 992px) {
  .order-layout {
    display: block;
  }

  .order-side {
    width: 100%;
    position: static;
  }

  .side-card {
    position: static;
    margin-top: 20px;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .hero-head,
  .inline-field {
    display: block;
  }

  .hero-price {
    margin-top: 16px;
  }

}
</style>



// WEBPACK FOOTER //
// src/pages/shop/order.vue